// Rush + Firm in-hands date checkboxes on the order (Stephanie 2026-10-12).
// The sales rep marks them; the Production Report highlights those POs (rush =
// inverted black row, firm = heavy black outline; an order can be both).
// Internal only — never shown to the client. Hidden until Jake runs the
// database update (GET /orders/:id/urgency answers available: false).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "@/lib/api";

interface Urgency {
  available: boolean;
  rush: boolean;
  firm: boolean;
}

export function OrderUrgencyToggles({ orderId, canEdit }: { orderId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const key = ["orders", orderId, "urgency"] as const;
  const [error, setError] = useState<string | null>(null);
  const q = useQuery({ queryKey: key, queryFn: () => api<Urgency>(`/orders/${orderId}/urgency`) });
  const save = useMutation({
    mutationFn: (patch: Partial<Pick<Urgency, "rush" | "firm">>) =>
      api<Urgency>(`/orders/${orderId}/urgency`, { method: "PUT", body: JSON.stringify(patch) }),
    onSuccess: (u) => {
      setError(null);
      qc.setQueryData(key, u);
      void qc.invalidateQueries({ queryKey: ["production", "jobs"] });
    },
    onError: (err) => {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setError(body?.error ?? "We couldn't save that. Try again.");
    },
  });

  if (!q.data?.available) return null;
  const box = (field: "rush" | "firm", label: string, hint: string) => (
    <label className="inline-flex min-h-[40px] cursor-pointer items-center gap-2 text-sm text-ink">
      <input
        type="checkbox"
        className="h-4 w-4 accent-ink"
        checked={q.data[field]}
        disabled={!canEdit || save.isPending}
        onChange={(e) => save.mutate({ [field]: e.target.checked })}
      />
      <span className="font-medium">{label}</span>
      <span className="text-xs text-muted">{hint}</span>
    </label>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1" aria-label="Production priority">
      {box("rush", "Rush", "Production sees this order highlighted")}
      {box("firm", "Firm in-hands date", "The date can't move")}
      {error && <p role="alert" className="w-full text-xs text-risk">{error}</p>}
    </div>
  );
}
